import { encode, useFormatAmount, useIntl } from '@capsicum/common';
import {
	Alert,
	AmountSelection,
	Button,
	ButtonSection,
	TextLineOrSkeleton,
	Tooltip,
	useRouter
} from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import { useWithdrawDataAtom, useWithdrawState } from '@capsicum/features/wallet/shared';
import { useEffect, useState } from 'react';
import { type FieldError } from 'react-hook-form';
import styled from 'styled-components';

export const AmountCHZ = () => {
	const { $t } = useIntl();
	const { push } = useRouter();
	const { formatAmount } = useFormatAmount();
	const { setCurrencyId, setInputAmount } = useWithdrawDataAtom();
	const [error, setError] = useState<FieldError | undefined>(undefined);

	const {
		chzAvailable,
		conversionAltPrice,
		conversionRates,
		convertedDefaultPrice,
		defaultCurrency,
		formatedConversionAvailable,
		formattedCHZAvailable,
		inputValue,
		isWithdrawLoading,
		minCHZ,
		onAmountChange,
		onRatesChanged
	} = useWithdrawState();

	const handleSubmit = () => {
		setCurrencyId(defaultCurrency?.id || '');
		setInputAmount(inputValue?.toString());
		push(`/wallet/withdraw/recipients`);
	};

	const isValueOutsideLimits =
		!inputValue ||
		!minCHZ ||
		!defaultCurrency?.rawBalance ||
		Number(inputValue) < minCHZ ||
		Number(inputValue) > chzAvailable;

	useEffect(() => {
		if (conversionRates) {
			onRatesChanged(conversionRates[0]?.rate);
		}
	}, [onRatesChanged, conversionRates]);

	useEffect(() => {
		if (isValueOutsideLimits) {
			setError({
				message: $t('wallet.withdraw.amountSelection.amountErrorCHZ', {
					maxValue: chzAvailable,
					minValue: Number(minCHZ)
				}),
				type: 'error'
			});
		} else {
			setError(undefined);
		}
	}, [isValueOutsideLimits]);

	return (
		<S.Container>
			<S.PageTitle>{$t('wallet.withdraw.amountSelection.titleCHZ')}</S.PageTitle>

			<S.BalanceText>
				{$t('wallet.withdraw.amountSelection.balance')}
				<S.HighlightedAmount>{formattedCHZAvailable}</S.HighlightedAmount>
				{encode('doubleTilde')}
				<TextLineOrSkeleton isLoading={isWithdrawLoading} lineWidth={60}>
					{formatedConversionAvailable}
				</TextLineOrSkeleton>

				<Tooltip contentPosition="bottom_right">
					<Tooltip.Trigger testID="withdrawCHZ-amount-subtitleTooltip" />
					<Tooltip.Content>
						<Tooltip.Description>
							{$t('wallet.withdraw.amountSelection.tooltipInfoCHZ')}
						</Tooltip.Description>
					</Tooltip.Content>
				</Tooltip>
			</S.BalanceText>
			<S.AmountSelectionContainer>
				<AmountSelection
					altCurrencyAmount={conversionAltPrice}
					conversionPrice={convertedDefaultPrice}
					currencyShortName={defaultCurrency?.shortCode}
					defaultCurrencyAmount={`1 ${defaultCurrency?.shortCode}`}
					error={error}
					isConversionRatesLoading={isWithdrawLoading}
					max={chzAvailable}
					min={Number(formatAmount(minCHZ))}
					testID="withdrawCHZ-amount-input"
					value={inputValue?.toString()}
					onChange={amount => onAmountChange(amount)}
				/>
			</S.AmountSelectionContainer>
			<S.AlertWrapper
				closable={false}
				icon={InfoIcon}
				subTitle={$t('wallet.withdraw.amountSelection.alertText')}
				testID="withdrawCHZ-amount-warning"
				title={$t('wallet.withdraw.amountSelection.alertTitle')}
				type="message"
			/>
			<ButtonSection>
				<Button
					color="brand"
					disabled={isWithdrawLoading || isValueOutsideLimits}
					isLoading={isWithdrawLoading}
					testID="withdrawCHZ-amount-submitButton"
					text={$t('wallet.withdraw.amountSelection.submitButton')}
					variant="default"
					onClick={handleSubmit}
				/>
			</ButtonSection>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.section`
		margin-top: ${({ theme }) => theme.spacings.xxlarge};
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		flex-flow: column;
		width: 100%;
		align-items: center;
		padding: ${({ theme }) => `0 ${theme.spacings.xsmall}`};
		${({ theme }) => theme.breakpoints.queries.only.tabletSmallDesktop} {
			margin: ${({ theme }) => `${theme.spacings.xxlarge} ${theme.spacings.xxxxlarge}`};
		}
	`;

	export const PageTitle = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: left;
		width: 100%;
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const BalanceText = styled.div`
		display: flex;
		align-items: center;
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: left;
		width: 100%;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const HighlightedAmount = styled.span`
		${({ theme }) => theme.fonts.text.text1}
		text-align: left;
	`;
	export const AmountSelectionContainer = styled.div`
		width: 100%;
	`;

	export const AlertWrapper = styled(Alert)`
		width: 100%;
	`;
}
